import { useEffect } from "react";
import AmenitiesRenderer from "@/components/tpr/AmenityIconRenderer/AmenitiesRenderer";
import Heading from "@/components/tpr/Heading/Heading";
import RoomGallery from "@/components/tpr/RoomGallery/RoomGallery";
import RoomSwatch from "@/components/tpr/RoomSwatch/RoomSwatch";
import { BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { useBreadcrumb } from "@/contexts/breadcrumbContext";
import { useRoomViewQuery } from "@/hooks/useRoomViewQuery";
import { ChevronDownIcon, ChevronLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router";
import type { Amenity } from "@/types/Amenity";
import { getLocationLabel } from "@/lib/utils";
import { formatRoomCapacity } from "@/utils/roomCapacity";

const RoomView = () => {
  const { setBreadcrumb } = useBreadcrumb();
  const navigate = useNavigate();
  const params = useParams();

  // Use React Query hook to fetch all data
  const { isLoading, data } = useRoomViewQuery({
    locationSlug: params.locationSlug,
    roomId: params.roomId,
  });

  const { location, allLocations, room, roomsInLocation } = data;

  useEffect(() => {
    setBreadcrumb(
      <>
        {/* <BreadcrumbItem className="text-sm text-gray-600">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5">
              Locations <ChevronDownIcon />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {allLocations.map((l) => (
                <DropdownMenuItem key={l.slug}>
                  <BreadcrumbLink onClick={() => navigate(`/location/${l.slug}`)}>{l.name}</BreadcrumbLink>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </BreadcrumbItem> */}
        <BreadcrumbItem className="text-sm text-gray-600">
          <BreadcrumbLink href={`/location/${location?.slug}`}>
            {location ? getLocationLabel(location) : "Location"}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem className="text-sm text-gray-600">
          <DropdownMenu>
            <DropdownMenuTrigger className="ml-2 flex items-center gap-1 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5">
              {room ? (
                <RoomSwatch swatch={room.swatch} name={room.name} />
              ) : (
                <span className="text-gray-400">Room</span>
              )}{" "}
              <ChevronDownIcon />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {roomsInLocation.map((r) => (
                <DropdownMenuItem key={r.id}>
                  <BreadcrumbLink onClick={() => navigate(`/location/${location?.slug}/room/${r.id}`)}>
                    <RoomSwatch swatch={r.swatch} name={r.name} />
                  </BreadcrumbLink>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </BreadcrumbItem>
      </>,
    );
    return () => setBreadcrumb(null);
  }, [location, room, allLocations, roomsInLocation, setBreadcrumb, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 border-2 border-gray-100 rounded-lg p-6 border-solid flex items-center justify-center">
        <Spinner className="size-6 mr-2" />
        <span className="text-lg">Loading location...</span>
      </div>
    );
  }

  return (
    <div className="px-4">
      <div className="mb-8 mt-8">
        <Heading title={`${location ? getLocationLabel(location) : ""} - ${room?.name || ""}`} />
      </div>

      <div
        className="w-full bg-slate-300 rounded-md p-2 mb-4 flex justify-between"
        data-testid="practice-bookings-toolbar"
      >
        <div className="flex justify-between flex-row w-full items-center">
          <div className="">
            <Button
              variant="link"
              onClick={() => navigate(-1)}
              className="text-base text-slate-800 hover:text-blue-800 hover:cursor-pointer"
            >
              <ChevronLeft />
              Go back
            </Button>
          </div>
          <div>{/* <Button variant="default">Book this room</Button> */}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
        <div className="xl:col-span-2 xl:order-1 order-2">
          <div className="bg-gray-50 border border-gray-200 rounded-lg shadow-sm dark:bg-gray-800 dark:border-gray-700">
            <div className="px-5 py-4">
              <dl className="text-gray-900 divide-y divide-gray-200 dark:text-white dark:divide-gray-700">
                <div className="flex flex-col md:flex-row pb-3">
                  <dt className="mb-1 mr-4 text-gray-500 md:text-lg dark:text-gray-400 md:w-38 md:min-w-38">
                    Description
                  </dt>
                  <dd className="text-base font-semibold">{room?.description || "No description available."}</dd>
                </div>
                <div className="flex flex-col md:flex-row py-3">
                  <dt className="mb-1 mr-4 text-gray-500 md:text-lg dark:text-gray-400 md:w-38 md:min-w-38">
                    Room Name
                  </dt>
                  <dd className="text-base font-semibold flex items-center gap-2">
                    <RoomSwatch swatch={room?.swatch ?? "bg-gray-500"} name={room?.name ?? "Room"} />
                    <span className="md:hidden">{room?.name}</span>
                  </dd>
                </div>
                <div className="flex flex-col md:flex-row py-3">
                  <dt className="mb-1 mr-4 text-gray-500 md:text-lg dark:text-gray-400 md:w-38 md:min-w-38">
                    Room Floor
                  </dt>
                  <dd className="text-base font-semibold capitalize">{room?.floor || "not specified"}</dd>
                </div>
                <div className="flex flex-col md:flex-row py-3">
                  <dt className="mb-1 mr-4 text-gray-500 md:text-lg dark:text-gray-400 md:w-38 md:min-w-38">
                    Capacity
                  </dt>
                  <dd className="text-base font-semibold">{formatRoomCapacity(room)}</dd>
                </div>
                <div className="flex flex-col md:flex-row py-3">
                  <dt className="mb-1 mr-4 text-gray-500 md:text-lg dark:text-gray-400 md:w-38 md:min-w-38">
                    Is accessible?
                  </dt>
                  <dd className="text-base font-semibold">
                    {room?.isAccessible ? "Yes, the room is accessible." : "No, the room is not accessible."}
                  </dd>
                </div>
                {room?.amenities && (room?.amenities as Amenity[]).filter((a) => !a.hidden).length > 0 && (
                  <div className="flex flex-col md:flex-row py-3">
                    <dt className="mb-1 mr-4 text-gray-500 md:text-lg dark:text-gray-400 md:w-38 md:min-w-38">
                      Amenities
                    </dt>
                    <dd className="text-base font-semibold">
                      <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                        <AmenitiesRenderer amenities={(room?.amenities as Amenity[]).filter((a) => !a.hidden)} />
                      </div>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>

        <div className="xl:col-span-1 xl:order-2 order-1">
          <div className="px-1 xl:min-h-64">
            <RoomGallery gallery={room?.gallery || []} />
          </div>
        </div>
      </div>
      {/* <div className="w-full my-8 lg:mx-auto lg:flex lg:justify-center">
        <Button variant="default" className="w-full lg:w-1/3">
          Book this room
        </Button>
      </div> */}
    </div>
  );
};

export default RoomView;
